import { LogLevel, LogService } from "@/services/logging";
import DeviceModule from "@/store/modules/device";
import POSConfigModule from "@/store/modules/pos-config";
import { checkManualHardwareUuid } from "../vue-helpers/common";
import { getDeviceLocation } from "@/api/gql/device";
import { isUserLoggedIn } from "../vue-helpers/user";
import { ICounter } from "@/types/db/transaction";
import ReceiptModule from "@/store/modules/receipt";
import {
  isFrenchCompliant,
  isSpanishCompliant,
} from "../vue-helpers/compliance";

interface IHardwareUUIDs {
  motherboardSerial?: string;
  biosUuid?: string;
  cpuId?: string;
  macAddress?: string;
}

async function sha256Hex(input: string): Promise<string> {
  const buffer = new TextEncoder().encode(input);
  const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Generates a v2 hardware hash.
 *
 * Why:
 *   v1 included the MAC address, which changes when users switch between
 *   wifi/ethernet, plug in an external hub, or otherwise alter their network
 *   topology. That caused legitimate devices to be flagged as new hardware
 *   and get sales blocked (see L3-7575). v2 only uses fields tied to the
 *   physical machine (motherboardSerial, biosUuid, cpuId).
 */
export async function generateHardwareHashV2(
  details: IHardwareUUIDs | null | undefined
): Promise<string> {
  if (!details) return "";
  const composite = [details.motherboardSerial, details.biosUuid, details.cpuId]
    .filter((value): value is string => !!value && value.length > 0)
    .join("_");
  if (!composite) return "";
  return `v2:${await sha256Hex(composite)}`;
}

/**
 * Synchronizes the current POS hardware with its assigned logical device (location).
 *
 * Why:
 *   Each physical POS machine generates a unique hardware identifier (hardwareHash)
 *   In order to track cash movements and device usage, each hardware unit
 *   must be linked to a logical "device" defined in the back office.
 *
 * How (lookup order):
 *   1. v2 hash (excludes MAC address, stable across network changes)
 *   2. v1 hash (legacy, includes MAC; can break when network topology changes)
 *   3. legacy hardwareUid (older installations)
 *
 *   - If a device location is found: store it locally
 *   - If no device location is found at all: clear any previously cached hardware location (to avoid stale data)
 *
 * Backward compatibility:
 *   Existing devices were registered with either v1 or the legacy hardwareUid.
 *   We keep both fallbacks so those devices still resolve until they are
 *   migrated to v2 in DB.
 */

export async function syncDeviceLocation(): Promise<void> {
  const groupId = POSConfigModule.groupId;
  if (!isUserLoggedIn.value || groupId == null) {
    LogService.info(
      "Device: Not updating hardware location as user is not logged in"
    );
    return;
  }

  if (!DeviceModule.hardwareInfo || !DeviceModule.hardwareInfo.id) {
    LogService.info("Device: Cannot link device, no hardware info available");
    return;
  }

  LogService.info("Device: hardware info:", {
    params: DeviceModule.hardwareInfo,
  });

  // We started manually injecting hardware uuid in localStorage of devices using TeamViewer
  // we need to call below function to rectify hardware uuid if needed
  checkManualHardwareUuid();
  const hardwareUid = DeviceModule.hardwareInfo.id;
  const hardwareHash = DeviceModule.getHardwareHash;
  const hardwareHashV2 = DeviceModule.getHardwareHashV2;

  let deviceLocation = null;

  // v2 hash: excludes MAC address, stable across network changes
  if (hardwareHashV2) {
    try {
      LogService.info(
        `Device: Going to fetch device location using v2 hash: ${hardwareHashV2}`,
        {
          params: { hardwareHashV2, groupId, hardwareUid },
        }
      );
      const res = await getDeviceLocation(hardwareHashV2, groupId);
      if (!res || !res.length) {
        LogService.info(
          `Device: No device location found for v2 hash: ${hardwareHashV2}, falling back to v1 hash`,
          {
            params: { hardwareHashV2, groupId, hardwareUid },
          }
        );
      } else {
        LogService.info(
          `Device: Device location pulled from backend via v2 hash: ${hardwareHashV2}`,
          {
            params: res,
          }
        );
        deviceLocation = res[0];
        if (deviceLocation.locationId) {
          DeviceModule.setHardwareLocation({
            locationId: deviceLocation.locationId,
            deviceName: deviceLocation.name || "",
            deviceId: Number(deviceLocation.id),
            deviceUid: deviceLocation.deviceUid || "",
            FiskalyCashRegisterId: deviceLocation.fiskalyCashRegisterId || "",
          });
        }
      }
    } catch (error) {
      LogService.info(
        `Device: Could not fetch device location from backend, v2 hash: ${hardwareHashV2}`,
        {
          error,
        }
      );
    }
  }

  // v1 hash: includes MAC, kept for backward compatibility
  if (!deviceLocation && hardwareHash) {
    try {
      LogService.info(
        `Device: Going to fetch device location using v1 hash: ${hardwareHash}`,
        {
          params: { hardwareHash, groupId, hardwareUid },
        }
      );
      const res = await getDeviceLocation(hardwareHash, groupId);
      if (!res || !res.length) {
        LogService.info(
          `Device: No device location found for v1 hash: ${hardwareHash}, falling back to hardwareUid`,
          {
            params: { hardwareHash, groupId, hardwareUid },
          }
        );
      } else {
        LogService.info(
          `Device: Device location pulled from backend via v1 hash: ${hardwareHash}`,
          {
            params: res,
          }
        );
        deviceLocation = res[0];

        // Migration flag: resolved via v1 while we can compute a v2 hash.
        // A future task will auto-migrate the stored hardware_uid; for now,
        // this log lets us identify devices that still need migration.
        if (hardwareHashV2) {
          LogService.info(
            `Device: MIGRATE_TO_V2 resolved via v1. deviceId=${deviceLocation.id} v1=${hardwareHash} v2=${hardwareHashV2}`,
            {
              params: {
                v1Hash: hardwareHash,
                v2Hash: hardwareHashV2,
                deviceId: deviceLocation.id,
                groupId,
              },
            }
          );
        }

        if (deviceLocation.locationId) {
          DeviceModule.setHardwareLocation({
            locationId: deviceLocation.locationId,
            deviceName: deviceLocation.name || "",
            deviceId: Number(deviceLocation.id),
            deviceUid: deviceLocation.deviceUid || "",
            FiskalyCashRegisterId: deviceLocation.fiskalyCashRegisterId || "",
          });
        }
      }
    } catch (error) {
      LogService.info(
        `Device: Could not fetch device location from backend, v1 hash: ${hardwareHash}`,
        {
          error,
        }
      );
    }
  }

  // Fallback: if neither v2 nor v1 hashes resolved, try the legacy hardwareUid
  if (!deviceLocation && hardwareUid) {
    try {
      LogService.info(
        `Device: Device has hardwareUid. Going to check if it is linked to a location in backend. HardwareUid: ${hardwareUid}`,
        {
          params: { hardwareUid, groupId },
        }
      );
      const res = await getDeviceLocation(hardwareUid, groupId);
      if (!res || !res.length) {
        LogService.info(
          `Device: No device location found for hardwareUid: ${hardwareUid}, clearing hardware location from localStorage`,
          {
            params: { hardwareUid, groupId },
          }
        );
        // Successful reposne but no device location found, let's clear it from localStorage in case it was set before
        // Why? Because we don't want to keep the device linked to an old location in localStorage
        DeviceModule.setHardwareLocation(null);
      } else {
        LogService.info(
          `Device: Device location pulled from backend (legacy). HardwareUid: ${hardwareUid}`,
          {
            params: res,
          }
        );
        deviceLocation = res[0];

        if (deviceLocation.locationId) {
          DeviceModule.setHardwareLocation({
            locationId: deviceLocation.locationId,
            deviceName: deviceLocation.name || "",
            deviceId: Number(deviceLocation.id),
            deviceUid: deviceLocation.deviceUid || "",
            FiskalyCashRegisterId: deviceLocation.fiskalyCashRegisterId || "",
          });
        }
      }
    } catch (error) {
      LogService.info(
        `Device: Could not fetch device location from backend, HardwareUid: ${hardwareUid}`,
        {
          error,
        }
      );
    }
  }

  if (deviceLocation) {
    // Counter logic:
    // Lets get the counter corresponding to this device
    try {
      LogService.info(
        `Receipt Counter: fetching receipt counter for this devie uuid: ${String(
          DeviceModule.hardwareLocation?.deviceUid
        )}`
      );
      const currentReceiptCounter: ICounter =
        await ReceiptModule.fetchReceiptCounter(
          String(DeviceModule.hardwareLocation?.deviceUid)
        );
      // Lets set it into the state
      LogService.info(
        `Receipt Counter: setting attached receipt counter for thethe device uuid: ${String(
          DeviceModule.hardwareLocation?.deviceUid
        )}`,
        { currentReceiptCounter }
      );
      ReceiptModule.setReceiptCounter(currentReceiptCounter);
    } catch (err) {
      LogService.info(
        "Receipt Counter: An error occurred trying to fetch receipt counter",
        {
          err,
        }
      );
    }
  }

  if (DeviceModule.hardwareLocation) {
    LogService.info(
      `Device: Device location set in localStorage, locationId: ${DeviceModule.hardwareLocation.locationId}, deviceId: ${DeviceModule.hardwareLocation.deviceId}`,
      {
        params: DeviceModule.hardwareLocation,
      }
    );
    // For FR clients that don't have the FR desktop version, we log and alert
    if (
      POSConfigModule.getGroupCountryCode == "FR" &&
      DeviceModule.hardwareInfo?.desktopAppVersion !==
        import.meta.env.VITE_DESKTOP_APP_VERSION_FR
    ) {
      LogService.info(
        `Alert: FR client without correct desktop app version, EFR compliance will not work. groupId=${groupId} hardwareUid=${hardwareUid} desktopAppVersion=${DeviceModule.hardwareInfo.desktopAppVersion}`,
        {
          params: {
            groupId,
            hardwareUid,
            desktopAppVersion: DeviceModule.hardwareInfo?.desktopAppVersion,
          },
        },
        LogLevel.Error
      );
    }
  } else {
    LogService.info(
      `Warning: device with no hardware location set in localStorage. hardwareUid: ${hardwareUid}, hash: ${hardwareHash}`
    );

    // For FR clients, no device location means the EFR compliance guard will
    // block all sales. We need to emit an "Alert:" to Slack.
    if (
      POSConfigModule.getGroupCountryCode == "FR" ||
      isSpanishCompliant.value
    ) {
      LogService.info(
        `Alert: FR/ES client has no device location. groupId=${groupId} hardwareUid=${hardwareUid} v1=${hardwareHash} v2=${hardwareHashV2}`,
        {
          params: {
            groupId,
            hardwareUid,
            v1Hash: hardwareHash,
            v2Hash: hardwareHashV2,
          },
        },
        LogLevel.Error
      );
    }

    // keep the old alert for FlipLab
    if (POSConfigModule.isFlipLab) {
      LogService.info("Warning: FlipLab device without hardware uid");
    }
  }
}

/**
 * Synchronizes the list of group locations from the backend.
 * This function fetches the latest locations associated with the group
 * and updates the local store accordingly.
 */
export async function syncGroupLocations(): Promise<void> {
  try {
    await DeviceModule.fetchLocations();
  } catch (error) {
    LogService.info("Device: Could not fetch group locations", { error });
  }
}
